/**
 * @sazeo/sdk — Addon installation hooks
 *
 * TanStack Query wrappers for the Addon platform. These call the
 * `/addons/*` Edge Functions (RLS-enforced; admin writes, member reads).
 *
 * Paired spec: specs/CODEX_PHASE4_WAVE1_ADDON_RUNTIME.md §2.3, §3.1
 */

import type {
  AddonInstallation,
  AddonInstallationCreateInput,
  AddonInstallationUpdateInput,
  AddonMarketplaceEntry,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const addonKeys = {
  all: ['addons'] as const,
  installations: () => [...addonKeys.all, 'installations'] as const,
  marketplace: () => [...addonKeys.all, 'marketplace'] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers (mirror sdk/settings.ts so response shape stays consistent)
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * useAddonInstallations — GET /addons/installations
 *
 * Returns all addon installations for the current tenant (RLS-scoped).
 * Any tenant member can read; only admin/owner can mutate.
 */
export function useAddonInstallations(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: addonKeys.installations(),
    queryFn: () => apiFetch<AddonInstallation[]>(supabase, 'addons/installations'),
  });
}

/**
 * useAddonMarketplace — GET /addons/marketplace
 *
 * Returns every addon the platform advertises (hardcoded for Wave 1).
 */
export function useAddonMarketplace(supabase: SupabaseClientLike) {
  return useQuery({
    queryKey: addonKeys.marketplace(),
    queryFn: () => apiFetch<AddonMarketplaceEntry[]>(supabase, 'addons/marketplace'),
  });
}

/**
 * useInstallAddon — POST /addons/installations (admin/owner only).
 */
export function useInstallAddon(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddonInstallationCreateInput) =>
      apiFetch<AddonInstallation>(supabase, 'addons/installations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: addonKeys.installations() });
    },
  });
}

/**
 * useUninstallAddon — DELETE /addons/installations/:addon_id (admin/owner only).
 */
export function useUninstallAddon(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (addonId: string) =>
      apiFetch<{ ok: true }>(supabase, `addons/installations/${addonId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: addonKeys.installations() });
    },
  });
}

/**
 * useToggleAddon — PATCH /addons/installations/:addon_id { enabled }.
 */
export function useToggleAddon(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addonId, enabled }: { addonId: string; enabled: boolean }) =>
      apiFetch<AddonInstallation>(supabase, `addons/installations/${addonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled } satisfies AddonInstallationUpdateInput),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: addonKeys.installations() });
    },
  });
}

/**
 * useUpdateAddonSettings — PATCH /addons/installations/:addon_id { settings }.
 *
 * Merges into the existing `settings` jsonb server-side; caller passes only
 * the delta.
 */
export function useUpdateAddonSettings(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      addonId,
      settings,
    }: {
      addonId: string;
      settings: Record<string, unknown>;
    }) =>
      apiFetch<AddonInstallation>(supabase, `addons/installations/${addonId}`, {
        method: 'PATCH',
        body: JSON.stringify({ settings } satisfies AddonInstallationUpdateInput),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: addonKeys.installations() });
    },
  });
}

// ---------------------------------------------------------------------------
// Attendance reporting hooks (L50)
// ---------------------------------------------------------------------------

export interface AttendanceReportRow {
  user_id: string;
  month_start: string;
  session_count: number;
  total_hours: number;
  user_name?: string | null;
}

export const attendanceReportKeys = {
  all: ['attendance-report'] as const,
  monthly: (tenantId: string, month: string) =>
    [...attendanceReportKeys.all, tenantId, month] as const,
} as const;

export function useAttendanceReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  month?: string,
  userId?: string,
  enabled = true,
) {
  const monthParam = month || new Date().toISOString().slice(0, 7);
  const params = new URLSearchParams({ month: monthParam });
  if (userId) params.set('user_id', userId);

  return useQuery({
    queryKey: attendanceReportKeys.monthly(tenantId, monthParam),
    queryFn: () =>
      apiFetch<AttendanceReportRow[]>(supabase, `addons/attendance/report?${params.toString()}`),
    enabled: Boolean(tenantId) && enabled,
  });
}
